import React, { useEffect, useRef } from 'react';
import { AppHostContainerBridge, BroadcastService, InviteService, BridgePort, HostDirectoryService } from 'mesh-microkernel';

interface AppContainerProps {
  appId: string;
  appUrl: string;
  broadcast: BroadcastService;
  invite: InviteService;
  directory: HostDirectoryService;
  /** Set true for apps that need getDisplayMedia() — omits the iframe sandbox entirely */
  unsandboxed?: boolean;
}

class DOMMessagePortWrapper implements BridgePort {
  private messageListeners = new Set<(message: unknown) => void>();
  private closeListeners = new Set<() => void>();

  constructor(private readonly port: MessagePort) {
    this.port.onmessage = (event) => {
      for (const listener of this.messageListeners) {
        listener(event.data);
      }
    };
  }

  postMessage(message: unknown): void {
    this.port.postMessage(message);
  }

  onMessage(handler: (message: unknown) => void): () => void {
    this.messageListeners.add(handler);
    return () => {
      this.messageListeners.delete(handler);
    };
  }

  close(): void {
    this.port.close();
    for (const listener of this.closeListeners) {
      listener();
    }
  }

  onClose(handler: () => void): () => void {
    this.closeListeners.add(handler);
    return () => {
      this.closeListeners.delete(handler);
    };
  }
}

export const AppContainer: React.FC<AppContainerProps> = ({ appId, appUrl, broadcast, invite, directory, unsandboxed = false }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<AppHostContainerBridge | null>(null);

  useEffect(() => {
    const handleWindowMessage = (event: MessageEvent) => {
      if (
        iframeRef.current &&
        event.source === iframeRef.current.contentWindow &&
        event.data?.type === 'REQUEST_PORT_HANDSHAKE'
      ) {
        // Create dedicated MessageChannel for sandboxed app
        const channel = new MessageChannel();
        const hostPort = new DOMMessagePortWrapper(channel.port1);

        // Instantiates DOM-free AppHostContainerBridge
        bridgeRef.current = new AppHostContainerBridge(
          appId,
          hostPort,
          broadcast,
          invite,
          directory,
          {
            publish: ['hts://mesh/chat/*', 'hts://mesh/whiteboard/*', 'hts://mesh/video/*', 'hts://mesh/screencast/*'],
            subscribe: ['hts://mesh/chat/*', 'hts://mesh/whiteboard/*', 'hts://mesh/video/*', 'hts://mesh/screencast/*'],
          }
        );

        // Transfer MessagePort 2 to iframe
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            { v: 1, type: 'HOST_HANDSHAKE_INIT' },
            '*',
            [channel.port2]
          );
          
          iframeRef.current.contentWindow.postMessage(
            { 
              v: 1, 
              type: 'CONFIG', 
              config: { nodeId: directory.getLocalNodeId() }
            },
            '*'
          );
        }
      }
    };

    window.addEventListener('message', handleWindowMessage);
    return () => {
      window.removeEventListener('message', handleWindowMessage);
      
      // Cleanup app-owned broadcasts
      const nodeId = directory.getLocalNodeId();
      directory.listBroadcasts().then(broadcasts => {
        for (const b of broadcasts) {
          if (b.publisherNodeId === nodeId && b.uri.includes(`/app/${appId}/`)) {
            directory.unregisterBroadcast(b.uri);
          }
        }
      });

      if (bridgeRef.current) {
        bridgeRef.current.teardown();
      }
    };
  }, [appId, appUrl, broadcast, invite, directory]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {/*
        We include 'allow-same-origin' so getUserMedia works inside the iframe.
        This is safe because:
        - The iframe content is served from the same origin.
        - The app cannot access host globals; communication is via MessagePort only.
      */}
      <iframe
        ref={iframeRef}
        src={appUrl}
        title={appId}
        {...(!unsandboxed && {
          sandbox: 'allow-scripts allow-same-origin allow-forms allow-popups'
        })}
        allow="camera; microphone; display-capture"
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          backgroundColor: '#090d16',
        }}
      />
    </div>
  );
};
